import {
  AvailableOrInstalledConnection,
  ConnectionConfig,
  ConnectionCreateRequest,
  ConnectionName,
  ConnectionUpdateRequest,
  InstalledConnection,
} from '../types'
import { AmazonConnectionConfig, AmazonUpdateConnection } from '../types/amazon'
import { CriteoConnectionConfig, CriteoConnectionUpdateRequest } from '../types/criteo'
import { DV360ConnectionConfig, DV360ConnectionUpdateRequest } from '../types/dv360'
import { FacebookConnectionConfig, FacebookConnectionUpdateRequest } from '../types/facebook'
import { FinishAuthRequest, FinishAuthType } from '../types/finishAuth'
import {
  GoogleAdwordsConnectionConfig,
  GoogleAdwordsConnectionUpdateRequest,
} from '../types/googleAdwords'
import {
  GoogleAnalyticsConnectionConfig,
  GoogleAnalyticsConnectionUpdateRequest,
} from '../types/googleAnalytics'
import {
  GoogleBigqueryConnectionConfig,
  GoogleBigqueryConnectionCreateRequest,
} from '../types/googleBigquery'
import {
  InnkeeprConnectionConfig,
  InnkeeprConnectionCreateRequest,
  InnkeeprConnectionUpdateRequest,
} from '../types/innkeepr'
import {
  TiktokConnectionConfig,
  TiktokConnectionUpdateRequest,
  TiktokFinishAuth,
} from '../types/tiktok'
import {
  WoocommerceConnectionConfig,
  WoocommerceConnectionCreateRequest,
  WoocommerceConnectionUpdateRequest,
} from '../types/woocommerce'
import { removeProtocol } from './innkeepr'

type ConnectionAuthInput = Pick<AvailableOrInstalledConnection, 'name'> & {
  config: ConnectionConfig
}

type ConnectionUpdateInput = Pick<InstalledConnection, 'name' | 'options' | 'status'> & {
  config: ConnectionConfig
}

export const toAuthEntity = (
  connection: ConnectionAuthInput,
  authorizationCode: string,
  redirectUri: string,
): FinishAuthRequest | undefined => {
  switch (connection.name) {
    case 'tiktok': {
      const config = connection.config as TiktokConnectionConfig
      return {
        advertiserIds: config.adAccountIds.split(';').filter(Boolean),
        authCode: authorizationCode,
        urlTrackingParam: config.adId,
        callbackUrl: redirectUri,
      } satisfies TiktokFinishAuth
    }
    default: {
      return {
        authCode: authorizationCode,
        callbackUrl: redirectUri,
      } satisfies FinishAuthType
    }
  }
}

export const toCreateEntity = (
  connectionName: ConnectionName,
  connectionConfig: ConnectionConfig,
): ConnectionCreateRequest | undefined => {
  switch (connectionName) {
    case 'googleBigquery': {
      const config = connectionConfig as GoogleBigqueryConnectionConfig
      return {
        project: config.project,
      } satisfies GoogleBigqueryConnectionCreateRequest
    }
    case 'innkeepr': {
      const config = connectionConfig as InnkeeprConnectionConfig
      const trackingUrl = removeProtocol(config.trackingUrl || 'cdn.innkeepr.ai')
      const loadBalancerUrl = config.trackingUrl || 'cdn.innkeepr.ai'

      return {
        loadBalancerUrl,
        trackingUrl,
      } satisfies InnkeeprConnectionCreateRequest
    }
    case 'woocommerce': {
      const config = connectionConfig as WoocommerceConnectionConfig
      return {
        consumerKey: config.apiKey,
        secret: config.apiSecret,
        shopBaseUrl: config.shopbaseUrl,
      } satisfies WoocommerceConnectionCreateRequest
    }
  }
}

export const toUpdateEntity = (
  connection: ConnectionUpdateInput,
): ConnectionUpdateRequest | undefined => {
  switch (connection.name) {
    case 'amazon': {
      const config = connection.config as AmazonConnectionConfig
      return {
        config: {
          amcInstanceId: config.amcInstanceId,
        },
      } satisfies AmazonUpdateConnection
    }
    case 'criteo': {
      const config = connection.config as CriteoConnectionConfig
      return {
        options: {
          ...connection.options,
          urlTrackingParam: config.adSetId,
        },
        config: {
          advertisers: config.advertisers,
          gumCallerId: config.gumCallerId,
        },
      } satisfies CriteoConnectionUpdateRequest
    }
    case 'dv360': {
      const config = connection.config as DV360ConnectionConfig
      return {
        options: {
          ...connection.options,
          urlTrackingParam: config.adGroupId,
        },
        config: {
          advertisers: config.advertisers,
        },
      } satisfies DV360ConnectionUpdateRequest
    }
    case 'facebook': {
      const config = connection.config as FacebookConnectionConfig
      return {
        options: {
          ...connection.options,
          urlTrackingParam: config.adId,
        },
        config: {
          adAccounts: config.accounts || [],
          pixelId: config.pixelId,
        },
      } satisfies FacebookConnectionUpdateRequest
    }
    case 'googleAdwords': {
      const config = connection.config as GoogleAdwordsConnectionConfig
      return {
        config: {
          adAccounts: config.accounts,
        },
        options: {
          ...connection.options,
          urlCampaignParam: config.campaignId,
        },
      } satisfies GoogleAdwordsConnectionUpdateRequest
    }
    case 'googleAnalytics': {
      const config = connection.config as GoogleAnalyticsConnectionConfig
      return {
        config: {
          trackingCredentials: {
            id: config.propertyId,
            measurementId: config.measurementId,
            secret: config.apiSecret,
          },
        },
      } satisfies GoogleAnalyticsConnectionUpdateRequest
    }
    case 'innkeepr': {
      const config = connection.config as InnkeeprConnectionConfig

      return {
        config,
        options: connection.options,
        status: 'active',
      } satisfies InnkeeprConnectionUpdateRequest
    }
    case 'tiktok': {
      const config = connection.config as TiktokConnectionConfig
      return {
        options: {
          ...connection.options,
          urlTrackingParam: config.adId,
        },
      } satisfies TiktokConnectionUpdateRequest
    }
    case 'woocommerce': {
      const config = connection.config as WoocommerceConnectionConfig
      return {
        config: {
          consumerKey: config.apiKey,
          secret: config.apiSecret,
          shopBaseUrl: config.shopbaseUrl,
        },
      } satisfies WoocommerceConnectionUpdateRequest
    }
  }
}
